import { Box, Checkbox, Tooltip, Typography, useTheme } from '@mui/material';
import { useMovieFinder, useSnack } from '@/logic/state';

import { CinemaModel } from '@/logic/services/cinemas';
import { FC } from 'react';

import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useIntl } from 'react-intl';
import ImageBoxFuture from '@/components/shared/image-box-future';

const CinemaFallbackImg = '/icons/cinema/emptyCinemaImgCard.svg';
const NotAllowedIcon = '/icons/shared/not-allowed-icon.svg';

interface CinemaCardProps {
  cinema: CinemaModel;
}

export const CinemaCard: FC<CinemaCardProps> = ({ cinema }) => {
  const theme = useTheme();
  const [selectedCinemas, setSelectedCinemas] = useMovieFinder(
    s => [s.selectedCinemas, s.setSelectedCinemas],
    shallow
  );
  const intl = useIntl();
  const setSnack = useSnack(s => s.setSnack, shallow);
  const getCinemaExperiences = (cinema: CinemaModel): string => {
    return cinema.experiences.map(experience => `${experience.name}`).toString();
  };

  const handleSelectCinema = ({ id, isDisabled }: CinemaModel): void => {
    if (isDisabled) {
      setSnack({
        isOpen: true,
        message: intl.formatMessage({
          id: 'movie_finder.not_available_option',
        }),
        icon: <ImageBoxFuture img={NotAllowedIcon} width='16px' />,
        type: 'info',
      });
      return;
    } else
      setSelectedCinemas(prevCinemas =>
        selectedCinemas.includes(id)
          ? [...prevCinemas.filter(cinemaId => cinemaId !== id)]
          : [...prevCinemas, id]
      );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.lightGrey.main}`,
        boxShadow: theme.customShadows.cardShadow,
        borderRadius: '20px',
        height: '106px',
      }}
    >
      <ImageBoxFuture
        img={cinema.image}
        title={cinema?.imageAlt || cinema?.name}
        fallBackImg={CinemaFallbackImg}
        width={'100%'}
        height={'100%'}
        sx={{
          borderRadius: '20px',
          overflow: 'hidden',
          height: '106px',
          width: '106px',
        }}
      />
      <Box
        sx={{
          paddingInlineStart: '22px',
          display: 'flex',
          justifyContent: 'space-between',
          flexGrow: 1,
        }}
      >
        <Box
          sx={{
            marginTop: '22px',
          }}
        >
          <Typography
            sx={{
              fontSize: {
                mobile: '18px',
                tablet: pxToRem(18),
              },
              color: theme.palette.grey[80],
              fontWeight: 700,
              fontFamily: theme.palette.typography.font.bold,
            }}
          >
            {cinema.name}
          </Typography>
          <Tooltip title={getCinemaExperiences(cinema)}>
            <Typography
              sx={{
                fontSize: {
                  mobile: '14px',
                  tablet: pxToRem(18),
                },
                color: theme.palette.grey[50],
                fontWeight: 400,
                fontFamily: theme.palette.typography.font.regular,
                whiteSpace: 'nowrap',
                // width: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: {
                  mobile: '100px',
                  tablet: '120px',
                  desktop: '320px',
                  mediumDesktop: '350px',
                },
                overflowWrap: 'anywhere',
              }}
            >
              {getCinemaExperiences(cinema)}
            </Typography>
          </Tooltip>
        </Box>

        <Box
          sx={{
            margin: pxToRem(18),
          }}
        >
          <Checkbox
            value={cinema.id}
            checked={
              selectedCinemas.find(cinemaId => cinemaId === cinema.id) ? true : false
            }
            indeterminateIcon={<IndeterminateCheckBoxIcon />}
            indeterminate={cinema.isDisabled}
            sx={{
              '&.Mui-disabled': {
                color: theme.palette.grey[30],
              },
              '&.MuiCheckbox-indeterminate': {
                color: theme.palette.grey[30],
              },
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            onChange={() => handleSelectCinema(cinema)}
          />
        </Box>
      </Box>
    </Box>
  );
};
